import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, CircularProgress } from "@mui/material";
import api from "../../utils/api";

const FeetDataTable = () => {
  const [feetData, setFeetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeetData, setSelectedFeetData] = useState(null);
  const [updatedFeet, setUpdatedFeet] = useState("");
  const [updatedSeconds, setUpdatedSeconds] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newFeet, setNewFeet] = useState("");
  const [newSeconds, setNewSeconds] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const navigate = useNavigate();

  const saveFeetData = async () => {
    try {
      const response = await api.post("/feet-data", {
        feet: newFeet,
        seconds: newSeconds,
      });

      if (response.data && response.data.data) {
        setFeetData([...feetData, response.data.data]);
        setNewFeet("");
        setNewSeconds("");
        setShowSaveDialog(false);
      } else {
        console.error("Error saving data:", response.data.message);
      }
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  const fetchFeetData = async () => {
    try {
      const response = await api.get("/get-feet-data");

      if (response.data && response.data.data) {
        setFeetData(response.data.data);
      } else {
        setFeetData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setFeetData([]);
    } finally {
      setLoading(false);
    }
  };

  const updateFeetData = async (id) => {
    try {
      const response = await api.put(`/update-feet-data/${id}`, {
        feet: updatedFeet,
        seconds: updatedSeconds,
      });

      if (response.data && response.data.data) {
        setFeetData(
          feetData.map((item) => (item._id === id ? response.data.data : item))
        );
        setUpdatedFeet("");
        setUpdatedSeconds("");
        setShowEditDialog(false);
        setSelectedFeetData(null);
      } else {
        console.error("Error updating data:", response.data.message);
      }
    } catch (error) {
      console.error("Error updating data:", error);
    }
  };

  const deleteFeetData = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this item?"
    );
    if (confirmDelete) {
      try {
        const response = await api.delete(`/delete-feet-data/${id}`);

        if (response.data && response.data.data) {
          setFeetData(feetData.filter((item) => item._id !== id));
        } else {
          console.error("Error deleting data:", response.data.message);
        }
      } catch (error) {
        console.error("Error deleting data:", error);
      }
    }
  };

  const handleEdit = (item) => {
    setSelectedFeetData(item);
    setUpdatedFeet(item.feet);
    setUpdatedSeconds(item.seconds);
    setShowEditDialog(true);
  };

  useEffect(() => {
    fetchFeetData();
  }, []);

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "0.6rem 0.75rem",
    fontSize: "1rem",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    outline: "none",
    marginTop: 6,
  };

  const dialogOverlay = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  };

  const dialogBox = {
    backgroundColor: "#fff",
    padding: 24,
    borderRadius: 14,
    boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
    minWidth: 320,
    maxWidth: "90%",
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          overflow: "hidden",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#667eea" }} />
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>Loading</span>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        background:
          "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        padding: "20px 32px 48px",
        boxSizing: "border-box",
      }}
    >
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <button
        onClick={() => setShowSaveDialog(true)}
        style={{
          position: "absolute",
          top: 24,
          right: 24,
          padding: "0.5rem 1.25rem",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
          zIndex: 10,
        }}
      >
        Add Data
      </button>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 32 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Disinfection Time
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          Manage feet and seconds settings for disinfection
        </p>
      </div>

      {/* Save Data Dialog */}
      {showSaveDialog && (
        <div style={dialogOverlay} onClick={() => setShowSaveDialog(false)}>
          <div style={dialogBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 20px", color: "#1e293b", fontSize: "1.25rem", fontWeight: 600 }}>
              Save Feet Data
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveFeetData();
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, color: "#64748b" }}>
                  Feet
                </label>
                <input
                  type="text"
                  value={newFeet}
                  onChange={(e) => setNewFeet(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, color: "#64748b" }}>
                  Seconds
                </label>
                <input
                  type="text"
                  value={newSeconds}
                  onChange={(e) => setNewSeconds(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: "0.6rem 1rem",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowSaveDialog(false)}
                  style={{
                    padding: "0.6rem 1rem",
                    backgroundColor: "#f1f5f9",
                    color: "#64748b",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Data Dialog */}
      {showEditDialog && (
        <div style={dialogOverlay} onClick={() => setShowEditDialog(false)}>
          <div style={dialogBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 20px", color: "#1e293b", fontSize: "1.25rem", fontWeight: 600 }}>
              Edit Feet Data
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateFeetData(selectedFeetData._id);
              }}
            >
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, color: "#64748b" }}>
                  Feet
                </label>
                <input
                  type="text"
                  value={updatedFeet}
                  onChange={(e) => setUpdatedFeet(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, color: "#64748b" }}>
                  Seconds
                </label>
                <input
                  type="text"
                  value={updatedSeconds}
                  onChange={(e) => setUpdatedSeconds(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: "0.6rem 1rem",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditDialog(false)}
                  style={{
                    padding: "0.6rem 1rem",
                    backgroundColor: "#f1f5f9",
                    color: "#64748b",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div
        style={{
          maxWidth: 720,
          margin: "0 auto",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#1e3a5f", color: "#fff" }}>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 13, fontWeight: 600 }}>
                Feet
              </th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontSize: 13, fontWeight: 600 }}>
                Seconds
              </th>
              <th style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, fontWeight: 600 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {feetData && feetData.length > 0 ? (
              feetData.map((item, index) => (
                <tr
                  key={item._id}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#fff" : "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <td style={{ padding: "12px 16px", fontSize: 14, color: "#334155" }}>
                    {item.feet} - feet
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 14, color: "#334155" }}>
                    {item.seconds}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <button
                      onClick={() => handleEdit(item)}
                      style={{
                        padding: "6px 12px",
                        marginRight: 8,
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteFeetData(item._id)}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#dc2626",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: 24, color: "#64748b", fontSize: 14 }}>
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeetDataTable;
